import { medications } from "./medications";

export function getSuggestedMedications({
  procedure,
  allergies
}) {

  const hasPenicillinAllergy =
    allergies?.includes("Penicilina");

  switch (procedure) {

    case "extraccion":
      return hasPenicillinAllergy
        ? [
            "Clindamicina",
            "Ibuprofeno",
            "Clorhexidina 0.12%"
          ]
        : [
            "Amoxicilina",
            "Ibuprofeno",
            "Clorhexidina 0.12%"
          ];

    case "cirugia":
      return hasPenicillinAllergy
        ? [
            "Clindamicina",
            "Ibuprofeno",
            "Dexametasona",
            "Clorhexidina 0.12%"
          ]
        : [
            "Amoxicilina",
            "Ibuprofeno",
            "Dexametasona",
            "Clorhexidina 0.12%"
          ];

    case "tercerMolar":
      return hasPenicillinAllergy
        ? [
            "Clindamicina",
            "Ibuprofeno",
            "Dexametasona",
            "Clorhexidina 0.12%"
          ]
        : [
            "Amoxicilina",
            "Ibuprofeno",
            "Dexametasona",
            "Clorhexidina 0.12%"
          ];

    case "endodoncia":
      return hasPenicillinAllergy
        ? [
            "Clindamicina",
            "Ibuprofeno"
          ]
        : [
            "Amoxicilina",
            "Ibuprofeno"
          ];

    case "absceso":
      return hasPenicillinAllergy
        ? [
            "Clindamicina",
            "Ibuprofeno",
            "Clorhexidina 0.12%"
          ]
        : [
            "Amoxicilina",
            "Ibuprofeno",
            "Clorhexidina 0.12%"
          ];

    case "periodoncia":
      return hasPenicillinAllergy
        ? [
            "Clindamicina",
            "Ibuprofeno",
            "Clorhexidina 0.12%"
          ]
        : [
            "Amoxicilina",
            "Ibuprofeno",
            "Clorhexidina 0.12%"
          ];

    case "implante":
      return hasPenicillinAllergy
        ? [
            "Clindamicina",
            "Ibuprofeno",
            "Dexametasona",
            "Clorhexidina 0.12%"
          ]
        : [
            "Amoxicilina",
            "Ibuprofeno",
            "Dexametasona",
            "Clorhexidina 0.12%"
          ];

    default:
      return [];
  }
}

export function isPediatricPatient(patient) {
  return Number(patient?.edad) <= 12;
}

/**
 * Estima el peso pediátrico cuando no existe
 * un peso registrado.
 *
 * Para pacientes de 1 a 10 años se utiliza
 * la fórmula APLS publicada por la AAP:
 *
 * peso (kg) = 2 × (edad + 4)
 *
 * El resultado se marca como estimado para
 * que la interfaz pueda informar al odontólogo.
 */
export function estimatePediatricWeight(age) {

  const edad = Number(age);

  if (
    !Number.isFinite(edad) ||
    edad < 1 ||
    edad > 10
  ) {
    return {
      available: false,
      weight: null,
      source: null,
      reason:
        "No existe un peso estimado automático validado para esta edad."
    };
  }

  const weight =
    2 * (edad + 4);

  return {
    available: true,
    weight,
    source: "estimated",
    method: "APLS",
    reason:
      "Peso estimado por edad. Confirmar peso real antes de prescribir."
  };
}

export function findMedicationByName(name) {
  return medications.find(
    medication =>
      medication.name === name
  );
}

/**
 * Devuelve las sugerencias de medicamentos según el procedimiento
 * y la alergia a penicilina.
 *
 * IMPORTANTE:
 * Se conserva el formato original: devuelve un array de nombres.
 */
export function calculatePediatricDose({

  medication,

  age,

  weight

}) {

  const edad = Number(age);

  if (!medication) {

    return {

      blocked: true,

      reason:
        "Medicamento no disponible."

    };

  }

  if (
    !Number.isFinite(edad) ||
    edad < 0
  ) {

    return {

      blocked: true,

      reason:
        "Edad pediátrica no válida."

    };

  }

  /*
   * ---------------------------------------------------------
   * PESO
   * ---------------------------------------------------------
   *
   * Si el peso real no está disponible,
   * intenta utilizar el peso pediátrico estimado.
   */

  let peso = Number(weight);

  let weightSource = "actual";

  let weightEstimated = false;

  if (
    !Number.isFinite(peso) ||
    peso <= 0
  ) {

    const estimated =
      estimatePediatricWeight(edad);

    if (!estimated.available) {

      return {

        blocked: true,

        requiresClinicalReview: true,

        reason:
          estimated.reason

      };

    }

    peso =
      estimated.weight;

    weightSource =
      estimated.source;

    weightEstimated = true;

  }

  /*
   * ---------------------------------------------------------
   * CONFIGURACIÓN PEDIÁTRICA
   * ---------------------------------------------------------
   */

  const pediatricDose =
    medication.pediatricDose;

  if (!pediatricDose) {

    return {

      blocked: true,

      requiresClinicalReview: true,

      reason:
        "No existe una pauta pediátrica configurada para este medicamento."

    };

  }

  /*
   * Medicamentos que no deben calcularse automáticamente.
   */

  if (
    pediatricDose.autoCalculate === false ||
    pediatricDose.calculation === "manual" ||
    pediatricDose.calculation === "not-recommended"
  ) {

    return {

      blocked: true,

      requiresClinicalReview: true,

      calculation:
        pediatricDose.calculation,

      note:
        pediatricDose.note ||
        "Este medicamento requiere valoración clínica pediátrica."

    };

  }

  /*
   * ---------------------------------------------------------
   * EDAD MÍNIMA
   * ---------------------------------------------------------
   */

  if (
    pediatricDose.minAgeMonths != null &&
    edad * 12 <
      pediatricDose.minAgeMonths
  ) {

    return {

      blocked: true,

      requiresClinicalReview: true,

      reason:
        "El paciente está por debajo de la edad mínima configurada para este medicamento."

    };

  }

  if (
    pediatricDose.minAgeYears != null &&
    edad <
      pediatricDose.minAgeYears
  ) {

    return {

      blocked: true,

      requiresClinicalReview: true,

      reason:
        "El paciente está por debajo de la edad mínima configurada para este medicamento."

    };

  }

  /*
   * ---------------------------------------------------------
   * CONCENTRACIÓN
   * ---------------------------------------------------------
   *
   * Ejemplos:
   *
   * 100mg/5mL
   * 250mg/5mL
   * 400mg/57mg/5mL
   *
   * En presentaciones combinadas se utiliza
   * el primer componente en mg.
   */

  const parseConcentration =
    presentation => {

      if (!presentation) {

        return null;

      }

      const text =
        String(presentation)
          .replace(/\s+/g, "");

      const match =
        text.match(
          /(\d+(?:\.\d+)?)mg(?:\/\d+(?:\.\d+)?mg)?\/(\d+(?:\.\d+)?)mL/i
        );

      if (!match) {

        return null;

      }

      const mg =
        Number(match[1]);

      const ml =
        Number(match[2]);

      if (
        !Number.isFinite(mg) ||
        !Number.isFinite(ml) ||
        mg <= 0 ||
        ml <= 0
      ) {

        return null;

      }

      return {

        mg,

        ml

      };

    };

  const concentration =
    parseConcentration(
      medication.pediatricPresentation
    );

  /*
   * ---------------------------------------------------------
   * CONVERSIÓN DE MG A ML
   * ---------------------------------------------------------
   */

  const calculateMl =
    doseMg => {

      if (
        !concentration ||
        !Number.isFinite(doseMg)
      ) {

        return null;

      }

      return roundDose(

        (
          doseMg *
          concentration.ml
        ) /
        concentration.mg,

        1

      );

    };

  /*
   * ---------------------------------------------------------
   * MG/KG/DOSIS
   * ---------------------------------------------------------
   */

  if (
    pediatricDose.calculation ===
    "mg/kg/dose"
  ) {

    let doseMgMin =
      peso *
      Number(
        pediatricDose.doseMinMgKg
      );

    let doseMgMax =
      peso *
      Number(
        pediatricDose.doseMaxMgKg
      );

    if (
      !Number.isFinite(doseMgMin) ||
      !Number.isFinite(doseMgMax)
    ) {

      return {

        blocked: true,

        requiresClinicalReview: true,

        reason:
          "No fue posible calcular la dosis pediátrica."

      };

    }

    /*
     * Límite máximo por dosis.
     */

    if (
      pediatricDose.maxSingleDoseMg != null
    ) {

      doseMgMin =
        Math.min(
          doseMgMin,
          Number(
            pediatricDose.maxSingleDoseMg
          )
        );

      doseMgMax =
        Math.min(
          doseMgMax,
          Number(
            pediatricDose.maxSingleDoseMg
          )
        );

    }

    const doseMlMin =
      calculateMl(doseMgMin);

    const doseMlMax =
      calculateMl(doseMgMax);

    const result = {

      blocked: false,

      calculation:
        "mg/kg/dose",

      weightUsed:
        peso,

      weightSource,

      weightEstimated,

      duration:
        pediatricDose.duration ??
        medication.duration,

      doseMgMin:
        roundDose(doseMgMin),

      doseMgMax:
        roundDose(doseMgMax),

      interval:
        pediatricDose.interval ||
        medication.interval,

      concentration

    };

    /*
     * Si existe presentación líquida,
     * también se devuelve la dosis en mL.
     */

    if (
      doseMlMin != null &&
      doseMlMax != null
    ) {

      result.doseMlMin =
        doseMlMin;

      result.doseMlMax =
        doseMlMax;

      /*
       * Dosis principal utilizada por la interfaz.
       *
       * Se mantiene en el límite inferior calculado.
       * No se inventa un redondeo clínico.
       */

      result.doseMl =
        doseMlMin;

    }

    return result;

  }

  /*
   * ---------------------------------------------------------
   * MG/KG/DÍA
   * ---------------------------------------------------------
   */

  if (
    pediatricDose.calculation ===
    "mg/kg/day"
  ) {

    let dailyMgMin =
      peso *
      Number(
        pediatricDose.doseMinMgKgDay
      );

    let dailyMgMax =
      peso *
      Number(
        pediatricDose.doseMaxMgKgDay
      );

    if (
      !Number.isFinite(dailyMgMin) ||
      !Number.isFinite(dailyMgMax)
    ) {

      return {

        blocked: true,

        requiresClinicalReview: true,

        reason:
          "No fue posible calcular la dosis pediátrica diaria."

      };

    }

    const interval =
      pediatricDose.interval ||
      medication.interval;

    const administrations =
      getAdministrationsPerDay(
        interval
      );

    if (!administrations) {

      return {

        blocked: true,

        requiresClinicalReview: true,

        reason:
          "No fue posible determinar la frecuencia de administración."

      };

    }

    /*
     * Límite máximo diario absoluto.
     */

    if (
      pediatricDose.maxDailyDoseMg != null
    ) {

      dailyMgMin =
        Math.min(
          dailyMgMin,
          Number(
            pediatricDose.maxDailyDoseMg
          )
        );

      dailyMgMax =
        Math.min(
          dailyMgMax,
          Number(
            pediatricDose.maxDailyDoseMg
          )
        );

    }

    /*
     * Límite máximo diario expresado
     * como mg/kg/día.
     */

    if (
      pediatricDose.maxDailyMgKg != null
    ) {

      const maxDailyByWeight =
        peso *
        Number(
          pediatricDose.maxDailyMgKg
        );

      dailyMgMin =
        Math.min(
          dailyMgMin,
          maxDailyByWeight
        );

      dailyMgMax =
        Math.min(
          dailyMgMax,
          maxDailyByWeight
        );

    }

    /*
     * Convertimos la dosis diaria
     * a dosis individual.
     */

    let doseMgMin =
      dailyMgMin /
      administrations;

    let doseMgMax =
      dailyMgMax /
      administrations;

    /*
     * Límite máximo por dosis.
     */

    if (
      pediatricDose.maxSingleDoseMg != null
    ) {

      doseMgMin =
        Math.min(
          doseMgMin,
          Number(
            pediatricDose.maxSingleDoseMg
          )
        );

      doseMgMax =
        Math.min(
          doseMgMax,
          Number(
            pediatricDose.maxSingleDoseMg
          )
        );

    }

    const doseMlMin =
      calculateMl(doseMgMin);

    const doseMlMax =
      calculateMl(doseMgMax);

    const result = {

      blocked: false,

      calculation:
        "mg/kg/day",

      weightUsed:
        peso,

      weightSource,

      weightEstimated,

      duration:
        pediatricDose.duration ??
        medication.duration,

      dailyMgMin:
        roundDose(dailyMgMin),

      dailyMgMax:
        roundDose(dailyMgMax),

      doseMgMin:
        roundDose(doseMgMin),

      doseMgMax:
        roundDose(doseMgMax),

      interval,

      administrations,

      concentration

    };

    /*
     * Si existe presentación líquida,
     * también se devuelve la dosis en mL.
     */

    if (
      doseMlMin != null &&
      doseMlMax != null
    ) {

      result.doseMlMin =
        doseMlMin;

      result.doseMlMax =
        doseMlMax;

      /*
       * Dosis principal utilizada por la interfaz.
       */

      result.doseMl =
        doseMlMin;

    }

    return result;

  }

  /*
   * ---------------------------------------------------------
   * DOSIS FIJA
   * ---------------------------------------------------------
   */

  if (
    pediatricDose.calculation ===
    "fixed"
  ) {

    return {

      blocked: false,

      calculation:
        "fixed",

      dose:
        pediatricDose.dose,

      interval:
        pediatricDose.interval ||
        medication.interval,

      duration:
        pediatricDose.duration ??
        medication.duration,

      instructions:
        pediatricDose.instructions,

      concentration

    };

  }

  /*
   * ---------------------------------------------------------
   * MODALIDAD NO IMPLEMENTADA
   * ---------------------------------------------------------
   */

  return {

    blocked: true,

    requiresClinicalReview: true,

    reason:
      "La modalidad de cálculo pediátrico no está implementada."

  };

}
export function calculatePediatricDoseByName({
  medicationName,
  age,
  weight
}) {
  const medication =
    findMedicationByName(
      medicationName
    );

  if (!medication) {
    return {
      applicable: false,
      blocked: true,
      requiresClinicalReview: true,
      reason:
        `No se encontró el medicamento "${medicationName}".`
    };
  }

  return calculatePediatricDose({
    medication,
    age,
    weight
  });
}


/**
 * Procesa todas las sugerencias de un procedimiento
 * para un paciente determinado.
 *
 * Para adultos conserva las sugerencias sin
 * modificar su comportamiento.
 *
 * Para pediatría agrega la información calculada
 * sin utilizar automáticamente la dosis adulta.
 */
export function buildMedicationSuggestions({
  procedure,
  allergies,
  age,
  weight
}) {
  const medicationNames =
    getSuggestedMedications({
      procedure,
      allergies
    });

  const pediatric =
    isPediatricPatient({
      edad: age
    });

  return medicationNames.map(
    medicationName => {
      const medication =
        findMedicationByName(
          medicationName
        );

      if (!medication) {
        return {
          name: medicationName,
          pediatric,
          blocked: false,
          requiresClinicalReview: false
        };
      }

      if (!pediatric) {
        return {
          ...medication,
          name: medicationName,
          pediatric: false,
          blocked: false,
          requiresClinicalReview: false
        };
      }

      const pediatricResult =
        calculatePediatricDose({
          medication,
          age,
          weight
        });

      return {
        ...medication,
        name: medicationName,
        pediatric: true,
        pediatricDoseResult:
          pediatricResult,

        blocked:
          pediatricResult.blocked,

        requiresClinicalReview:
          pediatricResult.requiresClinicalReview
      };
    }
  );
}


/**
 * Convierte un intervalo expresado como:
 *
 * 4h  -> 6 administraciones/día
 * 6h  -> 4 administraciones/día
 * 8h  -> 3 administraciones/día
 * 12h -> 2 administraciones/día
 * 24h -> 1 administración/día
 */
function getAdministrationsPerDay(
  interval
) {
  if (!interval) return null;

  const normalized =
    String(interval)
      .trim()
      .toLowerCase();

  const match =
    normalized.match(
      /(\d+(?:\.\d+)?)\s*h/
    );

  if (!match) return null;

  const hours =
    Number(match[1]);

  if (
    !Number.isFinite(hours) ||
    hours <= 0
  ) {
    return null;
  }

  const administrations =
    24 / hours;

  if (
    !Number.isFinite(
      administrations
    )
  ) {
    return null;
  }

  return administrations;
}


/**
 * Redondeo seguro para cantidades de medicamento.
 *
 * Evita cantidades con demasiados decimales,
 * pero no convierte automáticamente a tabletas,
 * cucharaditas o mL porque la presentación real
 * debe determinar esa conversión posteriormente.
 */
function roundDose(value) {
  if (!Number.isFinite(value)) {
    return null;
  }

  return Math.round(
    value * 100
  ) / 100;
}